import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton, Divider } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { Close, CheckCircle, Lock, ChevronRight, PhoneIphone, EventAvailable } from '@material-ui/icons';
import { useCampfireTheme } from '../../theme/useCampfireTheme';
import { useTenantSetupContext } from './useTenantSetupContext';
import { SETUP_STAGES, SetupStageKey } from './TenantSetupContext';
import {
  AISetupDialog,
  ProgramsSetupDialog,
  ActivitiesSetupDialog,
  ReviewSetupDialog,
  InviteVolunteerDialog,
  DownloadAppDialog,
} from '../onboarding-tool';
import { QuestionnaireDialog, QuestionnaireAnswers } from './QuestionnaireDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 8,
      maxWidth: 520,
    },
    header: {
      padding: '24px 24px 0 24px',
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      color: theme.palette.text.secondary,
    },
    content: {
      padding: '16px 24px 24px 24px',
    },
    progressSection: {
      marginBottom: 24,
    },
    progressBarBg: {
      height: 6,
      borderRadius: 3,
      backgroundColor: theme.palette.grey[200],
      marginTop: 8,
    },
    progressBarFill: {
      height: '100%',
      borderRadius: 3,
      backgroundColor: theme.palette.primary.main,
      transition: 'width 0.3s ease',
    },
    stageItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      marginBottom: 8,
      borderRadius: 8,
      border: `1px solid ${theme.palette.grey[200]}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.palette.grey[50],
        borderColor: theme.palette.grey[300],
      },
    },
    stageItemCompleted: {
      backgroundColor: 'rgba(76, 175, 80, 0.06)',
      borderColor: 'rgba(76, 175, 80, 0.3)',
    },
    stageItemCurrent: {
      backgroundColor: 'rgba(121, 111, 255, 0.06)',
      borderColor: 'rgba(121, 111, 255, 0.4)',
    },
    stageItemLocked: {
      opacity: 0.5,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent',
        borderColor: theme.palette.grey[200],
      },
    },
    stepNumber: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 600,
      marginRight: 12,
      flexShrink: 0,
    },
    stepNumberPending: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.grey[600],
    },
    stepNumberCurrent: {
      backgroundColor: '#796fff',
      color: '#ffffff',
    },
    stepNumberCompleted: {
      backgroundColor: 'transparent',
      color: theme.palette.success.main,
    },
    stepNumberLink: {
      backgroundColor: 'rgba(121, 111, 255, 0.12)',
      color: '#796fff',
    },
    stageContent: {
      flex: 1,
    },
    stageLabel: {
      fontWeight: 600,
      fontSize: 15,
      letterSpacing: '-0.02em',
      color: theme.palette.text.primary,
    },
    stageLabelCompleted: {
      color: theme.palette.text.secondary,
    },
    stageAction: {
      marginLeft: 8,
      color: theme.palette.grey[400],
      display: 'flex',
      alignItems: 'center',
    },
    actions: {
      padding: '16px 24px 24px 24px',
      justifyContent: 'space-between',
    },
    secondaryButton: {
      color: theme.palette.text.secondary,
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 14,
    },
    primaryButton: {
      backgroundColor: '#796fff',
      color: '#ffffff',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 14,
      padding: '8px 20px',
      borderRadius: 6,
      '&:hover': {
        backgroundColor: '#635ac7',
      },
    },
  })
);

interface TrialSetupDialogProps {
  open: boolean;
  onClose: () => void;
}

export function TrialSetupDialog({ open, onClose }: TrialSetupDialogProps) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const {
    completedStages,
    progressPercentage,
    completeStage,
    saveQuestionnaireAnswers,
    questionnaireAnswers,
    markDialogViewed,
    setupProgress,
  } = useTenantSetupContext();

  const [activeAIStage, setActiveAIStage] = useState<SetupStageKey | null>(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [isSubmittingQuestionnaire, setIsSubmittingQuestionnaire] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDownloadAppDialog, setShowDownloadAppDialog] = useState(false);

  const handleClose = () => {
    markDialogViewed();
    onClose();
  };

  const isStageCompleted = (stageKey: string) => completedStages.includes(stageKey);

  const isStageUnlocked = (stageIndex: number) => {
    if (stageIndex === 0) return true;
    const previousStage = SETUP_STAGES[stageIndex - 1];
    return isStageCompleted(previousStage.key);
  };

  const getStageData = (stageKey: string): Record<string, unknown> | null => {
    return setupProgress?.stageData?.[stageKey] ?? null;
  };

  const handleStageClick = (stageKey: SetupStageKey, stageIndex: number) => {
    const isLinkStage = linkStages.includes(stageKey);
    if (!isLinkStage && !isStageUnlocked(stageIndex)) return;
    if (isStageCompleted(stageKey) && !alwaysOpenStages.includes(stageKey)) return;

    if (stageKey === 'intro') {
      setShowQuestionnaire(true);
      return;
    }

    if (stageKey === 'programs' || stageKey === 'tasks' || stageKey === 'activities' || stageKey === 'review') {
      setActiveAIStage(stageKey);
      return;
    }

    if (stageKey === 'invite') {
      setShowInviteDialog(true);
      return;
    }

    if (stageKey === 'downloadApp') {
      setShowDownloadAppDialog(true);
      return;
    }

    if (stageKey === 'bookCall') {
      window.open('https://calendar.app.google/dwZBhWKzBSrEzvsx5', '_blank', 'noopener,noreferrer');
      completeStage(stageKey);
      return;
    }

    completeStage(stageKey);
  };

  const handleQuestionnaireComplete = async (answers: QuestionnaireAnswers) => {
    setIsSubmittingQuestionnaire(true);
    const success = await saveQuestionnaireAnswers(answers);
    setIsSubmittingQuestionnaire(false);
    if (success) {
      setShowQuestionnaire(false);
    }
  };

  const handleAISetupComplete = (data?: Record<string, unknown>) => {
    if (activeAIStage) {
      completeStage(activeAIStage, data ? { data } : undefined);
      setActiveAIStage(null);
    }
  };

  const handleAISetupClose = () => {
    setActiveAIStage(null);
  };

  const alwaysOpenStages: string[] = ['review', 'invite', 'downloadApp', 'bookCall'];
  const linkStages: string[] = ['downloadApp', 'bookCall'];
  const linkStageIcons: Record<string, React.ReactNode> = {
    downloadApp: <PhoneIphone style={{ fontSize: 16 }} />,
    bookCall: <EventAvailable style={{ fontSize: 16 }} />,
  };

  const getStepNumberClass = (stageKey: string, stageIndex: number) => {
    if (isStageCompleted(stageKey)) return `${classes.stepNumber} ${classes.stepNumberCompleted}`;
    if (isStageUnlocked(stageIndex)) return `${classes.stepNumber} ${classes.stepNumberCurrent}`;
    return `${classes.stepNumber} ${classes.stepNumberPending}`;
  };

  const getStageItemClass = (stageKey: string, stageIndex: number) => {
    if (isStageCompleted(stageKey) && !alwaysOpenStages.includes(stageKey)) {
      return `${classes.stageItem} ${classes.stageItemCompleted}`;
    }
    if (isStageCompleted(stageKey) && alwaysOpenStages.includes(stageKey)) {
      return classes.stageItem;
    }
    if (!isStageUnlocked(stageIndex)) return `${classes.stageItem} ${classes.stageItemLocked}`;
    return `${classes.stageItem} ${classes.stageItemCurrent}`;
  };

  const getNextIncompleteStage = () => {
    return SETUP_STAGES.findIndex((s) => !completedStages.includes(s.key));
  };

  return (
    <>
      <Dialog
        open={open && !activeAIStage && !showQuestionnaire && !showInviteDialog && !showDownloadAppDialog}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <Box className={classes.header}>
          <IconButton className={classes.closeButton} onClick={handleClose} size='small'>
            <Close fontSize='small' />
          </IconButton>
          <Typography
            style={{
              fontWeight: 700,
              fontSize: 24,
              letterSpacing: '-0.02em',
              color: '#343434',
            }}
          >
            Welcome to Volaby!
          </Typography>
          <Typography
            style={{
              color: theme.color.grey.neutralBrand400,
              marginTop: 4,
              fontSize: 14,
              maxWidth: '80%',
              lineHeight: 1.3,
            }}
          >
            Let&apos;s get your organisation setup on Volaby using
            <br />
            our intelligent onboarding tool
          </Typography>
        </Box>

        <DialogContent className={classes.content}>
          <Box className={classes.progressSection}>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Typography style={{ fontWeight: 600, fontSize: 13, color: theme.color.grey.neutral400 }}>
                Setup Progress
              </Typography>
              <Typography style={{ fontWeight: 600, fontSize: 13, color: theme.color.grey.neutral400 }}>
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <Box className={classes.progressBarBg}>
              <Box className={classes.progressBarFill} style={{ width: `${progressPercentage}%` }} />
            </Box>
          </Box>

          <Box>
            {SETUP_STAGES.filter((s) => !linkStages.includes(s.key)).map((stage, index) => {
              const completed = isStageCompleted(stage.key);
              const unlocked = isStageUnlocked(index);
              const isAlwaysOpen = alwaysOpenStages.includes(stage.key);
              const showAsCompleted = completed && !isAlwaysOpen;

              return (
                <Box
                  key={stage.key}
                  className={getStageItemClass(stage.key, index)}
                  onClick={() => handleStageClick(stage.key, index)}
                >
                  <Box className={getStepNumberClass(stage.key, index)}>
                    {completed ? <CheckCircle style={{ fontSize: 24 }} /> : index + 1}
                  </Box>
                  <Box className={classes.stageContent}>
                    <Typography
                      className={`${classes.stageLabel} ${showAsCompleted ? classes.stageLabelCompleted : ''}`}
                    >
                      {stage.label}
                    </Typography>
                  </Box>
                  <Box className={classes.stageAction}>
                    {!unlocked ? (
                      <Lock style={{ fontSize: 18 }} />
                    ) : isAlwaysOpen ? (
                      <ChevronRight style={{ fontSize: 20 }} />
                    ) : !completed ? (
                      <ChevronRight style={{ fontSize: 20 }} />
                    ) : null}
                  </Box>
                </Box>
              );
            })}

            <Divider style={{ margin: '16px 0' }} />

            {SETUP_STAGES.filter((s) => linkStages.includes(s.key)).map((stage) => {
              const completed = isStageCompleted(stage.key);

              return (
                <Box key={stage.key} className={classes.stageItem} onClick={() => handleStageClick(stage.key, -1)}>
                  <Box
                    className={`${classes.stepNumber} ${
                      completed ? classes.stepNumberCompleted : classes.stepNumberLink
                    }`}
                  >
                    {completed ? (
                      <CheckCircle style={{ fontSize: 24 }} />
                    ) : linkStageIcons[stage.key] ? (
                      linkStageIcons[stage.key]
                    ) : null}
                  </Box>
                  <Box className={classes.stageContent}>
                    <Typography className={classes.stageLabel}>{stage.label}</Typography>
                  </Box>
                  <Box className={classes.stageAction}>
                    <ChevronRight style={{ fontSize: 20 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions className={classes.actions}>
          {getNextIncompleteStage() < 0 ? (
            <>
              <Box />
              <Button className={classes.primaryButton} onClick={handleClose}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button className={classes.secondaryButton} onClick={handleClose}>
                I&apos;ll do this later
              </Button>
              <Button
                className={classes.primaryButton}
                onClick={() => {
                  const nextIndex = getNextIncompleteStage();
                  if (nextIndex >= 0) {
                    const nextStage = SETUP_STAGES[nextIndex];
                    handleStageClick(nextStage.key, nextIndex);
                  }
                }}
              >
                Start
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <QuestionnaireDialog
        open={showQuestionnaire}
        onClose={() => setShowQuestionnaire(false)}
        onComplete={handleQuestionnaireComplete}
        initialAnswers={questionnaireAnswers as QuestionnaireAnswers | undefined}
        isSubmitting={isSubmittingQuestionnaire}
      />

      <ProgramsSetupDialog
        open={activeAIStage === 'programs'}
        onClose={handleAISetupClose}
        onComplete={handleAISetupComplete}
        questionnaireAnswers={questionnaireAnswers ?? undefined}
      />
      <AISetupDialog
        open={activeAIStage === 'tasks'}
        onClose={handleAISetupClose}
        onComplete={handleAISetupComplete}
        questionnaireAnswers={questionnaireAnswers ?? undefined}
      />
      <ActivitiesSetupDialog
        open={activeAIStage === 'activities'}
        onClose={handleAISetupClose}
        onComplete={handleAISetupComplete}
        questionnaireAnswers={questionnaireAnswers ?? undefined}
      />
      <ReviewSetupDialog
        open={activeAIStage === 'review'}
        onClose={handleAISetupClose}
        onComplete={(data) => handleAISetupComplete(data)}
        questionnaireAnswers={questionnaireAnswers ?? undefined}
        completedStages={completedStages}
        savedResults={getStageData('review') as Record<string, unknown> | null}
      />
      <InviteVolunteerDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onComplete={() => {
          completeStage('invite');
          setShowInviteDialog(false);
        }}
      />
      <DownloadAppDialog
        open={showDownloadAppDialog}
        onClose={() => setShowDownloadAppDialog(false)}
        onComplete={() => {
          completeStage('downloadApp');
          setShowDownloadAppDialog(false);
        }}
      />
    </>
  );
}
